import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, AirplaneTilt, Bed, Stamp, ShieldCheck, MapTrifold, ArrowUpRight } from "@phosphor-icons/react";
import Hero from "@/components/Hero";
import WhyChooseUs from "@/components/WhyChooseUs";
import DestinationsGrid from "@/components/DestinationsGrid";
import Testimonials from "@/components/Testimonials";
import Newsletter from "@/components/Newsletter";
import { DESTINATIONS, PACKAGES, SITE, waLink } from "@/lib/site";

const SERVICES_PREVIEW = [
    { icon: AirplaneTilt, title: "Flight Booking" },
    { icon: Bed, title: "Hotel Booking" },
    { icon: Stamp, title: "Visa Assistance" },
    { icon: ShieldCheck, title: "Travel Insurance" },
    { icon: MapTrifold, title: "Custom Tours" },
];

const PROCESS_STEPS = [
    { num: "01", title: "Tell us your dream", text: "A quick call or message — share where you want to go, how you want to feel, and your rough dates." },
    { num: "02", title: "We craft your itinerary", text: "Within 48 hours, a dedicated concierge sends you a hand-built itinerary with curated hotels, guides and experiences." },
    { num: "03", title: "Refine together", text: "We iterate until every detail feels right. No pressure, no rush — just a plan you're genuinely excited about." },
    { num: "04", title: "Travel, effortlessly", text: "We handle every booking, transfer and surprise. You just show up and experience it." },
];

export default function Home() {
    return (
        <div data-testid="page-home">
            <Hero />

            {/* Services strip */}
            <section className="py-20 bg-[#080E20] border-y border-white/5" data-testid="services-strip">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-y-10 gap-x-6">
                        {SERVICES_PREVIEW.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <motion.div
                                    key={s.title}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.05 }}
                                    className="flex flex-col items-center text-center gap-3"
                                >
                                    <Icon size={32} weight="light" className="text-gold" />
                                    <p className="font-sans text-xs uppercase tracking-[0.25em] text-white/70">{s.title}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Destinations */}
            <section className="py-24 lg:py-32 bg-navy" data-testid="home-destinations">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
                        <div>
                            <p className="overline">Where we love sending people</p>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium mt-4 leading-tight max-w-2xl">
                                Destinations, <em className="italic text-gold">handpicked.</em>
                            </h2>
                        </div>
                        <Link
                            to="/destinations"
                            data-testid="home-see-all-destinations"
                            className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-widest hover:text-[#E5C158]"
                        >
                            See all destinations <ArrowRight size={14} weight="bold" />
                        </Link>
                    </div>
                    <DestinationsGrid destinations={DESTINATIONS.slice(0, 5)} layout="home" />
                </div>
            </section>

            <WhyChooseUs />

            {/* How it works */}
            <section className="py-24 lg:py-32 bg-navy border-t border-white/5" data-testid="home-process">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
                        <div className="lg:col-span-6">
                            <p className="overline">How it works</p>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium mt-4 leading-tight">
                                Four steps to your <em className="italic text-gold">perfect escape.</em>
                            </h2>
                        </div>
                        <p className="lg:col-span-6 font-sans text-base text-white/65 leading-relaxed">
                            We've refined our process over a decade. It's personal, unhurried, and built entirely around you.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
                        {PROCESS_STEPS.map((step, i) => (
                            <motion.div
                                key={step.num}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.6, delay: i * 0.08 }}
                                className="p-8 lg:p-10 border-r border-b border-white/10 group hover:bg-white/[0.02] transition-colors duration-500"
                            >
                                <p className="font-serif text-5xl text-gold/20 leading-none">{step.num}</p>
                                <h3 className="font-serif text-xl mt-5">{step.title}</h3>
                                <p className="font-sans text-sm text-white/60 mt-3 leading-relaxed">{step.text}</p>
                            </motion.div>
                        ))}
                    </div>
                    <div className="mt-12 flex flex-wrap gap-4">
                        <a
                            href={waLink("Hi! I'd like to start planning a trip with Midasis.")}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-[#E5C158] px-7 py-3.5 font-sans font-medium uppercase tracking-wider text-xs transition-colors"
                        >
                            Start planning <ArrowRight size={14} weight="bold" />
                        </a>
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 border border-white/25 hover:border-gold hover:text-gold px-7 py-3.5 font-sans font-medium uppercase tracking-wider text-xs transition-colors"
                        >
                            Talk to a concierge
                        </Link>
                    </div>
                </div>
            </section>

            {/* Featured packages */}
            <section className="py-24 lg:py-32 bg-[#080E20]" data-testid="home-packages">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
                        <div>
                            <p className="overline">Signature Packages</p>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium mt-4 leading-tight max-w-2xl">
                                A season of <em className="italic text-gold">considered escapes.</em>
                            </h2>
                        </div>
                        <Link
                            to="/packages"
                            data-testid="home-see-all-packages"
                            className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-widest hover:text-[#E5C158]"
                        >
                            All packages <ArrowRight size={14} weight="bold" />
                        </Link>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                        {PACKAGES.slice(0, 2).map((p) => (
                            <Link
                                key={p.id}
                                to="/packages"
                                data-testid={`home-pkg-${p.id}`}
                                className="group relative overflow-hidden aspect-[5/6] border border-white/10"
                            >
                                <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105" />
                                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/20 to-transparent" />
                                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                                    <p className="overline">{p.nights}</p>
                                    <h3 className="font-serif text-3xl lg:text-4xl mt-3">{p.name}</h3>
                                    <p className="font-sans text-gold mt-3">{p.price} <span className="text-white/60 text-xs ml-1">{p.perPerson}</span></p>
                                </div>
                                <div className="absolute top-6 right-6 w-10 h-10 border border-white/25 grid place-items-center group-hover:bg-gold group-hover:text-navy group-hover:border-gold transition-all">
                                    <ArrowRight size={16} weight="bold" />
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* Remaining packages as list */}
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {PACKAGES.slice(2).map((p) => (
                            <Link
                                key={p.id}
                                to="/packages"
                                className="group flex items-center gap-4 border border-white/10 p-5 hover:border-gold/40 transition-colors"
                            >
                                <img src={p.image} alt={p.name} className="w-16 h-16 object-cover shrink-0" />
                                <div className="min-w-0">
                                    <p className="font-serif text-base leading-snug truncate">{p.name}</p>
                                    <p className="font-sans text-xs text-gold mt-1">{p.price}</p>
                                    <p className="font-sans text-[11px] text-white/50 mt-0.5">{p.nights}</p>
                                </div>
                                <ArrowUpRight size={14} className="text-white/30 group-hover:text-gold shrink-0 ml-auto transition-colors" />
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <Testimonials />
            <Newsletter />
        </div>
    );
}
